import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_BASE_URL } from "../config";
import { setError } from "./errorSlice";

//
export const fetchRoomBatchSections = createAsyncThunk(
  "batchSection/fetchRoomBatchSections",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      const response = await fetch(`${API_BASE_URL}/api/RoomBatchSection`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch batch sections");
      return await response.json();
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);
export const fetchBatchSections = createAsyncThunk(
  "batchSection/fetchBatchSections",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      const response = await fetch(`${API_BASE_URL}/api/BatchSection`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch batch sections");
      return await response.json();
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const createBatchSection = createAsyncThunk(
  "batchSection/createBatchSection",
  async (batchSectionData, { rejectWithValue, getState, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      // Get current state to access batches, sections, and terms
      const state = getState();
      const { batches, sections, terms } = state.batchSection;

      // Find the selected objects to get full details
      const selectedBatch = batches.find(
        (b) => b.batchId === batchSectionData.batchId
      );
      const selectedSection = sections.find(
        (s) => s.sectionId === batchSectionData.sectionId
      );
      const selectedTerm = terms.find(
        (t) => t.termId === batchSectionData.termId
      );

      const payload = {
        termId: batchSectionData.termId,
        batchId: batchSectionData.batchId,
        sectionId: batchSectionData.sectionId,
        term: {
          termId: batchSectionData.termId,
          name: selectedTerm?.name || "Default Term",
          description: selectedTerm?.description || "Default Term Description",
          state: true,
        },
        batch: {
          batchId: batchSectionData.batchId,
          batchName: selectedBatch?.batchName || "Default Batch",
          schoolId: selectedBatch?.schoolId || null,
          school: selectedBatch?.school || null,
          batchGradeScales: selectedBatch?.batchGradeScales || [],
          state: true,
        },
        section: {
          sectionId: batchSectionData.sectionId,
          name: selectedSection?.name || "Default Section",
          state: true,
        },
        state: true,
        createdDate: new Date().toISOString(),
        updatedDate: new Date().toISOString(),
      };

      const response = await fetch(`${API_BASE_URL}/api/BatchSection`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create batch section");
      }

      return await response.json();
    } catch (error) {
      dispatch(setError(error.message ));
      return rejectWithValue(error.message);
    }
  }
);

export const updateBatchSection = createAsyncThunk(
  "batchSection/updateBatchSection",
  async (
    { id, ...batchSectionData },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      // Get current state to access batches, sections, and terms
      const state = getState();
      const { batches, sections, terms } = state.batchSection;

      // Find the selected objects to get full details
      const selectedBatch = batches.find(
        (b) => b.batchId === batchSectionData.batchId
      );
      const selectedSection = sections.find(
        (s) => s.sectionId === batchSectionData.sectionId
      );
      const selectedTerm = terms.find(
        (t) => t.termId === batchSectionData.termId
      );

      const payload = {
        bsid: id,
        termId: batchSectionData.termId,
        batchId: batchSectionData.batchId,
        sectionId: batchSectionData.sectionId,
        term: {
          termId: batchSectionData.termId,
          name: selectedTerm?.name || "Default Term",
          description: selectedTerm?.description || "Default Term Description",
          state: true,
        },
        batch: {
          batchId: batchSectionData.batchId,
          batchName: selectedBatch?.batchName || "Default Batch",
          schoolId: selectedBatch?.schoolId || null,
          school: selectedBatch?.school || null,
          batchGradeScales: selectedBatch?.batchGradeScales || [],
          state: true,
        },
        section: {
          sectionId: batchSectionData.sectionId,
          name: selectedSection?.name || "Default Section",
          state: true,
        },
        state: true,
        createdDate: batchSectionData.createdDate || new Date().toISOString(),
        updatedDate: new Date().toISOString(),
      };

      const response = await fetch(`${API_BASE_URL}/api/BatchSection/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update batch section");
      }

      return await response.json();
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const deleteBatchSection = createAsyncThunk(
  "batchSection/deleteBatchSection",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }
      // Ensure id is a string
      const batchSectionId = id.toString();

      /*       console.log("Delete Request Details:", {
        url: `${API_BASE_URL}/api/BatchSection/${batchSectionId}`,
        method: "DELETE",
        id: batchSectionId,
        idType: typeof batchSectionId,
      }); */

      const response = await fetch(
        `${API_BASE_URL}/api/BatchSection/${batchSectionId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Log response details comprehensively
      /*       console.log("Delete Response:", {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
      });
 */
      // Try to get response body
      const responseBody = await response.text();
      //      console.log("Delete Response Body:", responseBody);

      // Check if response is not ok
      if (!response.ok) {
        console.error("Delete Error Response:", responseBody);
        throw new Error(`Failed to delete batch section: ${responseBody}`);
      }

      // Return the id of the deleted batch section
      return batchSectionId;
    } catch (error) {
      console.error("Delete Batch Section Error:", error);
      dispatch(setError(error.message || "Failed to delete batch section"));
      return rejectWithValue(error.message || "Failed to delete batch section");
    }
  }
);

const batchSectionSlice = createSlice({
  name: "batchSection",
  initialState: {
    batchSections: [],
    roomBatchSections: [],
    loading: false,
    error: null,
    editingBatchSection: null,
    modalVisible: false,
  },
  reducers: {
    setEditingBatchSection(state, action) {
      state.editingBatchSection = action.payload;
      state.modalVisible = true;
    },
    clearEditingBatchSection(state) {
      state.editingBatchSection = null;
      state.modalVisible = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoomBatchSections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoomBatchSections.fulfilled, (state, action) => {
        state.loading = false;
        state.roomBatchSections = action.payload?.roomBatchSections;
 //            console.log("state.roomBatchSections", state.roomBatchSections);
 //           console.log("Array.isArray(sections)", Array.isArray(state.roomBatchSections));
      })
      .addCase(fetchRoomBatchSections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchBatchSections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBatchSections.fulfilled, (state, action) => {
        state.loading = false;
        state.batchSections = action.payload;
 //       console.log("action.payload", action.payload);
      })
      .addCase(fetchBatchSections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createBatchSection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBatchSection.fulfilled, (state, action) => {
        state.loading = false;
        state.batchSections.push(action.payload);
        state.modalVisible = false;
        state.editingBatchSection = null;
      })
      .addCase(createBatchSection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateBatchSection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBatchSection.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.batchSections.findIndex(
          (bs) => bs.bsid === action.payload.bsid
        );
        if (index !== -1) {
          state.batchSections[index] = action.payload;
        }
        state.modalVisible = false;
        state.editingBatchSection = null;
      })
      .addCase(updateBatchSection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteBatchSection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBatchSection.fulfilled, (state, action) => {
        state.loading = false;
        state.batchSections = state.batchSections.filter(
          (bs) => bs.bsid !== action.payload
        );
      })
      .addCase(deleteBatchSection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setEditingBatchSection, clearEditingBatchSection } =
  batchSectionSlice.actions;
export default batchSectionSlice.reducer;
